import { useState } from 'react'
import { Outlet, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { useTheme } from '@/client/components/theme-provider'
import { useSession, authClient } from '@/client/lib/auth'
import { useNavigate } from 'react-router-dom'
import { usePreferences, useUpdatePreferences } from '@/client/modules/settings/hooks/useSettings'
import { Menu, Moon, Sun, Search, Keyboard } from 'lucide-react'
import { getIcon } from '@/client/lib/icon-registry'
import { cn } from '@/lib/utils'
import { useAdminStatus } from '@/client/modules/settings/hooks/useAdminStatus'
import { useDynamicDashboardMenu, useDynamicUserAccountMenu, type DynamicMenuItem } from '@/client/hooks/useDynamicMenu'
import { GlobalSearch } from '@/client/modules/search/components/GlobalSearch'
import { useGlobalShortcuts, KeyboardHelpDialog } from '@/client/lib/keyboard'
import { NotificationCenter } from '@/client/modules/notifications/components/NotificationCenter'
import {
  SidebarSkeleton,
  SidebarError,
  EmptyMenuMessage,
  UserMenuSkeleton,
} from '@/client/components/shared/NavigationSkeleton'

/**
 * Props for navigation components
 */
interface NavComponentProps {
  items: DynamicMenuItem[]
  isActive: (item: DynamicMenuItem) => boolean
  isLoading?: boolean
  error?: Error | null
  onRetry?: () => void
}

/**
 * Render a single menu item link
 */
function MenuItemLink({
  item,
  isActive,
  onClick,
  className,
}: {
  item: DynamicMenuItem
  isActive: boolean
  onClick?: () => void
  className?: string
}) {
  const Icon = getIcon(item.icon)

  return (
    <Link
      to={item.href}
      onClick={onClick}
      className={cn(
        'flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors lg:gap-3 lg:py-2',
        isActive
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
        className
      )}
    >
      {Icon && <Icon className="h-4 w-4 shrink-0" />}
      <span className="truncate">{item.label}</span>
    </Link>
  )
}

/**
 * Render grouped sidebar navigation
 */
function GroupedNavigation({
  items,
  isActive,
}: {
  items: DynamicMenuItem[]
  isActive: (item: DynamicMenuItem) => boolean
}) {
  return (
    <div className="space-y-4">
      {items.map((item) => {
        // Category with children - render as group
        if (item.type === 'category' && item.children?.length) {
          return (
            <div key={item.id} className="space-y-1">
              {/* Group header */}
              <div className="px-3 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {item.label}
              </div>
              {/* Group items */}
              <div className="space-y-0.5 lg:space-y-1">
                {item.children.map((child) => (
                  <MenuItemLink
                    key={child.id}
                    item={child}
                    isActive={isActive(child)}
                  />
                ))}
              </div>
            </div>
          )
        }

        // Regular item (non-category)
        return (
          <MenuItemLink
            key={item.id}
            item={item}
            isActive={isActive(item)}
          />
        )
      })}
    </div>
  )
}

/**
 * Sidebar component - desktop navigation
 *
 * Responsive design:
 * - Full height with scrollable navigation area
 * - Compact mode on shorter screens (reduced padding/spacing)
 * - Custom scrollbar styling
 * - Supports grouped (categorized) menu items
 */
function Sidebar({ items, isActive, isLoading, error, onRetry }: NavComponentProps) {
  return (
    <aside className="fixed left-0 top-0 z-40 h-screen w-64 border-r border-border bg-background">
      <div className="flex h-full flex-col">
        {/* Logo - fixed height, doesn't shrink */}
        <div className="flex h-14 min-h-14 items-center border-b border-border px-6">
          <a href="https://l2stack.jezweb.com.au/" className="flex items-center gap-2 font-semibold text-lg" target="_blank" rel="noopener noreferrer">
            <span className="text-primary">⚡</span>
            <span>L2Stack</span>
          </a>
        </div>

        {/* Navigation - scrollable with responsive spacing */}
        <nav className="sidebar-nav flex-1 overflow-y-auto p-3 lg:p-4">
          {isLoading ? (
            <SidebarSkeleton />
          ) : error ? (
            <SidebarError error={error} onRetry={onRetry} />
          ) : items.length === 0 ? (
            <EmptyMenuMessage />
          ) : (
            <GroupedNavigation items={items} isActive={isActive} />
          )}
        </nav>
      </div>
    </aside>
  )
}

/**
 * Mobile sidebar - sheet component
 *
 * Scrollable navigation for mobile devices
 */
function MobileSidebar({ items, isActive, isLoading, error, onRetry }: NavComponentProps) {
  const [open, setOpen] = useState(false)

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden">
          <Menu className="h-5 w-5" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-64 p-0">
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-14 min-h-14 items-center border-b border-border px-6">
            <a
              href="https://l2stack.jezweb.com.au/"
              className="flex items-center gap-2 font-semibold text-lg"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="text-primary">⚡</span>
              <span>L2Stack</span>
            </a>
          </div>

          {/* Navigation - scrollable */}
          <nav className="sidebar-nav flex-1 overflow-y-auto p-3">
            {isLoading ? (
              <SidebarSkeleton />
            ) : error ? (
              <SidebarError error={error} onRetry={onRetry} />
            ) : items.length === 0 ? (
              <EmptyMenuMessage />
            ) : (
              <div className="space-y-4">
                {items.map((item) => {
                  // Category with children
                  if (item.type === 'category' && item.children?.length) {
                    return (
                      <div key={item.id} className="space-y-1">
                        <div className="px-3 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          {item.label}
                        </div>
                        <div className="space-y-0.5">
                          {item.children.map((child) => (
                            <MenuItemLink
                              key={child.id}
                              item={child}
                              isActive={isActive(child)}
                              onClick={() => setOpen(false)}
                            />
                          ))}
                        </div>
                      </div>
                    )
                  }

                  return (
                    <MenuItemLink
                      key={item.id}
                      item={item}
                      isActive={isActive(item)}
                      onClick={() => setOpen(false)}
                    />
                  )
                })}
              </div>
            )}
          </nav>
        </div>
      </SheetContent>
    </Sheet>
  )
}

/**
 * Header component - top navigation bar
 */
function Header({
  navItems,
  isNavActive,
  navLoading,
  navError,
  navRefetch,
  onSearchOpen,
  onKeyboardHelp,
}: {
  navItems: DynamicMenuItem[]
  isNavActive: (item: DynamicMenuItem) => boolean
  navLoading: boolean
  navError: Error | null
  navRefetch: () => void
  onSearchOpen: () => void
  onKeyboardHelp: () => void
}) {
  const { theme, setTheme } = useTheme()
  const { data: session } = useSession()
  const { data: preferences } = usePreferences()
  const { data: isAdmin } = useAdminStatus()
  const updatePreferences = useUpdatePreferences()
  const navigate = useNavigate()
  const {
    flatItems: userMenuItems,
    isLoading: userMenuLoading,
  } = useDynamicUserAccountMenu({ isAdmin: isAdmin ?? false })

  const handleSignOut = async () => {
    await authClient.signOut()
    navigate('/sign-in')
  }

  const toggleTheme = () => {
    if (session && preferences) {
      // Logged in: update preferences in database
      const currentMode = preferences.mode
      const newMode = currentMode === 'dark' ? 'light' : 'dark'

      updatePreferences.mutate({
        theme: preferences.theme,
        mode: newMode,
      })
    } else {
      // Not logged in: use localStorage
      setTheme(theme === 'dark' ? 'light' : 'dark')
    }
  }

  // Get user initials for avatar fallback
  const userInitials = session?.user?.name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase() || 'U'

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-border bg-background px-4 md:px-6">
      {/* Mobile menu toggle */}
      <MobileSidebar
        items={navItems}
        isActive={isNavActive}
        isLoading={navLoading}
        error={navError}
        onRetry={navRefetch}
      />

      {/* Search button */}
      <Button
        variant="outline"
        className="relative h-9 w-full justify-start text-sm text-muted-foreground sm:pr-12 md:w-40 lg:w-64"
        onClick={onSearchOpen}
      >
        <Search className="mr-2 h-4 w-4" />
        <span className="hidden lg:inline-flex">Search...</span>
        <span className="inline-flex lg:hidden">Search...</span>
        <kbd className="pointer-events-none absolute right-1.5 top-1.5 hidden h-6 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
          <span className="text-xs">⌘</span>K
        </kbd>
      </Button>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Notification center */}
      <NotificationCenter />

      {/* Keyboard shortcuts */}
      <Button variant="ghost" size="icon" onClick={onKeyboardHelp} title="Keyboard shortcuts">
        <Keyboard className="h-5 w-5" />
        <span className="sr-only">Keyboard shortcuts</span>
      </Button>

      {/* Theme toggle */}
      <Button variant="ghost" size="icon" onClick={toggleTheme}>
        {theme === 'dark' ? (
          <Sun className="h-5 w-5" />
        ) : (
          <Moon className="h-5 w-5" />
        )}
        <span className="sr-only">Toggle theme</span>
      </Button>

      {/* User menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-10 w-10 rounded-full">
            <Avatar className="h-10 w-10">
              <AvatarImage src={session?.user?.image || undefined} alt={session?.user?.name || 'User'} />
              <AvatarFallback>{userInitials}</AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">{session?.user?.name}</p>
              <p className="text-xs leading-none text-muted-foreground">
                {session?.user?.email}
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {userMenuLoading ? (
            <UserMenuSkeleton />
          ) : (
            userMenuItems.map((item, index) => {
              const Icon = getIcon(item.icon)
              const isSignOut = item.href === '/sign-out'
              const isAdminItem = item.href?.includes('/admin')
              const isCmsItem = item.href?.includes('/cms/')

              // Skip admin items if not admin (CMS might not filter these)
              if (isAdminItem && !isAdmin) return null

              // Add separator before CMS section
              const prevItem = userMenuItems[index - 1]
              const showCmsSeparator = isCmsItem && prevItem && !prevItem.href?.includes('/cms/')

              // Add separator before admin section and sign out
              const showSeparator = (isAdminItem || isSignOut) && prevItem && !prevItem.href?.includes('/admin') && prevItem.href !== '/sign-out' && !prevItem.href?.includes('/cms/')

              return (
                <div key={item.id}>
                  {showCmsSeparator && <DropdownMenuSeparator />}
                  {showSeparator && <DropdownMenuSeparator />}
                  <DropdownMenuItem
                    onClick={() => isSignOut ? handleSignOut() : navigate(item.href)}
                  >
                    {Icon && <Icon className="mr-2 h-4 w-4" />}
                    <span>{item.label}</span>
                  </DropdownMenuItem>
                </div>
              )
            })
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  )
}

/**
 * Dashboard layout wrapper
 * Provides consistent layout with sidebar and header for all dashboard pages
 */
export function DashboardLayout() {
  const navigate = useNavigate()
  const { data: isAdmin } = useAdminStatus()
  const {
    items: navItems,
    flatItems,
    isActive,
    isLoading: navLoading,
    error: navError,
    refetch: navRefetch,
  } = useDynamicDashboardMenu({
    isAdmin: isAdmin ?? false,
  })
  const [searchOpen, setSearchOpen] = useState(false)
  const [keyboardHelpOpen, setKeyboardHelpOpen] = useState(false)

  // Register global keyboard shortcuts
  useGlobalShortcuts({
    onSearch: () => setSearchOpen((open) => !open),
    onHelp: () => setKeyboardHelpOpen(true),
    onBack: () => navigate(-1),
    onForward: () => navigate(1),
  })

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Desktop sidebar */}
      <div className="hidden md:block">
        <Sidebar
          items={navItems}
          isActive={isActive}
          isLoading={navLoading}
          error={navError}
          onRetry={navRefetch}
        />
      </div>

      {/* Main content area */}
      <div className="flex flex-1 flex-col md:pl-64">
        <Header
          navItems={flatItems}
          isNavActive={isActive}
          navLoading={navLoading}
          navError={navError}
          navRefetch={navRefetch}
          onSearchOpen={() => setSearchOpen(true)}
          onKeyboardHelp={() => setKeyboardHelpOpen(true)}
        />

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-background p-4 md:p-6">
          <Outlet />
        </main>
      </div>

      {/* Global Search Command Palette */}
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />

      {/* Keyboard Shortcuts Help Dialog */}
      <KeyboardHelpDialog open={keyboardHelpOpen} onOpenChange={setKeyboardHelpOpen} />
    </div>
  )
}
